<template>
	<div class="flex flex-row flex-wrap">
		<div class="col-12 xl:col-12">
			<div class="card">

				<h4>Customer logos</h4>

				<div class="grid ml-0 mr-1 mb-2">
					<div class="flex flex-auto p-input-icon-left p-input-icon-right mt-1">
						<i class="pi pi-users" />
						<InputText v-model="customerListFilter" placeholder="Search customers..." />
						<i class="pi pi-search" />
					</div>
					<div class="flex align-items-center flex-auto mt-1 ml-2 logo-filter-container">
						<i class="pi pi-image" />
						<Multiselect :options="logoOptions"
									 :searchable="true"
									 :filter="true"
									 class="w-100pc"
									 v-model="logoFilter"
									 valueProp="customerLogoId"
									 label="logoName"
									 track-by="logoName"
									 placeholder="Filter by logo..." />
					</div>
					<div class="flex align-items-center">
						<a @click="exportReport" class="css-image excel-icon mt-1" v-tooltip.top="'Export to Excel'"></a>
					</div>
				</div>

				<div class="flex align-items-center gap-4 mb-2">
					<div class="flex align-items-center gap-2">
						<InputSwitch v-model="includeDeletedCustomers" @change="loadCustomers" />
						<a @click="includeDeletedCustomers = !includeDeletedCustomers; loadCustomers()">Show deleted customers</a>
					</div>
				</div>

				<div>
					<DataView :value="filteredCustomers"
							  datakey="companyId"
							  :paginator="true"
							  :rows="10"
							  :scrollable="true"
							  stripedRows
							  class="p-datatable-sm"
							  :paginatorTemplate="paginatorTemplate"
							  :rowsPerPageOptions="[10,20,50,100,200]"
							  responsiveLayout="scroll"
							  :alwaysShowPaginator="false"
							  currentPageReportTemplate="Showing {first} to {last} of {totalRecords}">

						<template #header>
							<div class="col-12">
								<div class="grid flex-nowrap">
									<div class="col-fixed col-id header-label sortable-header" @click="toggleSort('companyId')">
										Id <i :class="sortIconFor('companyId')" />
									</div>
									<div class="col header-label sortable-header" @click="toggleSort('companyName')">
										Customer <i :class="sortIconFor('companyName')" />
									</div>
									<div class="col-fixed col-logo header-label sortable-header" @click="toggleSort('logoName')">
										Logo (SVG only) <i :class="sortIconFor('logoName')" />
									</div>
									<div class="col-1 header-label sortable-header text-center" @click="toggleSort('displayCompassLogo')">
										Show Compass logo <i :class="sortIconFor('displayCompassLogo')" />
									</div>
									<div class="col-fixed col-actions text-right"></div>
								</div>
							</div>
						</template>

						<template #list="{data}">
							<div :class="{ 'col-12': true, 'row-deleted': data.isCompanyDeleted }">
								<div class="grid flex-nowrap hover-row align-items-center">
									<div class="col-fixed col-id">
										<!-- Deleted companies aren't clickable: GetCompanyQuery excludes soft-deleted records everywhere in the app, not just here. -->
										<a v-if="data.companyId && !data.isCompanyDeleted" @click.exact="navigation.toCompanyDetailsScreen(data.companyId)" @click.shift="navigation.toCompanyDetailsScreen(data.companyId, true)">{{data.companyId}}</a>
										<span v-else-if="data.companyId" class="text-color-secondary">{{data.companyId}}</span>
									</div>
									<div class="col">
										<a v-if="data.companyId && !data.isCompanyDeleted" @click.exact="navigation.toCompanyDetailsScreen(data.companyId)" @click.shift="navigation.toCompanyDetailsScreen(data.companyId, true)">{{data.companyName}}</a>
										<span v-else class="text-color-secondary">{{data.companyName}}</span>
										<Badge v-if="data.isCompanyDeleted" class="small-grey-badge ml-1" value="deleted" />
										<div v-if="data.propertiesWithOverrideCount > 0" class="customer-sub-line text-color-secondary">
											{{data.propertiesWithOverrideCount}} propert{{data.propertiesWithOverrideCount === 1 ? 'y overrides' : 'ies override'}} this
											<a class="ml-1" @click="syncProperties(data)">Sync all</a>
										</div>
										<div v-if="data.ownPropertiesWithLogo && data.ownPropertiesWithLogo.length > 0" class="customer-sub-line text-color-secondary">
											Bwof Template for
											<template v-for="(property, index) in visibleLogoRefs(data.ownPropertiesWithLogo, data.companyId, 'own')" :key="property.id">
												<a @click.exact="navigation.toPropertyDetailsScreen(property.id)" @click.shift="navigation.toPropertyDetailsScreen(property.id, true)">{{property.name}}</a><span v-if="index < visibleLogoRefs(data.ownPropertiesWithLogo, data.companyId, 'own').length - 1">, </span>
											</template>
											<a v-if="!isLogoListExpanded(data.companyId, 'own') && data.ownPropertiesWithLogo.length > inlineLogoListLimit" @click="expandLogoList(data.companyId, 'own')">...</a>
										</div>
										<div v-if="data.sharedWith && data.sharedWith.length > 0" class="customer-sub-line text-color-secondary">
											Also Bwof Template for
											<template v-for="(item, index) in visibleLogoRefs(data.sharedWith, data.companyId, 'shared')" :key="(item.isProperty ? 'p' : 'c') + item.id">
												<a @click.exact="item.isProperty ? navigation.toPropertyDetailsScreen(item.id) : navigation.toCompanyDetailsScreen(item.id)" @click.shift="item.isProperty ? navigation.toPropertyDetailsScreen(item.id, true) : navigation.toCompanyDetailsScreen(item.id, true)">{{item.name}}</a><span v-if="index < visibleLogoRefs(data.sharedWith, data.companyId, 'shared').length - 1">, </span>
											</template>
											<a v-if="!isLogoListExpanded(data.companyId, 'shared') && data.sharedWith.length > inlineLogoListLimit" @click="expandLogoList(data.companyId, 'shared')">...</a>
										</div>
									</div>
									<div class="col-fixed col-logo">
										<img v-if="data.bwofTemplateLogo" :src="logoPreviewSrc(data)" class="logo-thumbnail cursor-pointer" @click="openManageLogo(data)" v-tooltip.top="'Manage this logo'" />
										<a v-else @click="openCompassLogoPreview">Compass default</a>
									</div>
									<div class="col-1 text-center">
										<!-- Wrapped as disabled elements don't fire hover events for the tooltip -->
										<span v-tooltip.top="displayCompassLogoTooltip(data)">
											<InputSwitch :disabled="!canSetDisplayCompassLogo(data)"
														 v-model="data.displayCompassLogo"
														 @change="setDisplayCompassLogo(data)" />
										</span>
									</div>
									<div class="col-fixed col-actions text-right">
										<SplitButton v-if="data.companyId" :label="data.bwofTemplateLogo ? 'Update' : 'Upload'" icon="pi pi-upload" class="p-button-sm" :model="uploadMenuItemsFor(data)" @click="chooseFile(data)" />
									</div>
								</div>
							</div>
						</template>
					</DataView>
				</div>
			</div>
		</div>

		<input type="file" ref="fileInput" accept=".svg" class="hidden" @change="onFileSelected" />

		<!-- Sync all properties to the logo -->
		<Dialog v-model:visible="showSyncConfirm" :modal="true" header="Sync properties" class="confirm-dialog">
			<p class="sync-summary">
				{{syncAffectedProperties.length}} propert{{syncAffectedProperties.length === 1 ? 'y' : 'ies'}} will be updated to use {{syncCustomer ? syncCustomer.companyName : 'this customer'}}'s logo, including
			</p>
			<p v-if="syncAffectedProperties.length > 0" class="flex align-items-center gap-2">
				<b>View all:</b> <a @click="exportSyncAffectedProperties" class="css-image excel-icon" v-tooltip.top="'Export full list to Excel'"></a>
			</p>
			<p>Continue?</p>
			<template #footer>
				<Button label="No" icon="pi pi-times" class="cancel" @click="cancelSyncConfirm" />
				<Button label="Continue" icon="pi pi-check" @click="confirmSync" />
			</template>
		</Dialog>

		<!-- Assign an existing logo -->
		<Dialog v-model:visible="showSelectExisting" :modal="true" header="Update logo" class="confirm-dialog">
			<p>Assign an existing logo to {{selectExistingCustomer ? selectExistingCustomer.companyName : 'this customer'}}.</p>
			<Multiselect :options="logoOptions"
						 :searchable="true"
						 :filter="true"
						 class="w-100pc"
						 v-model="selectExistingLogoId"
						 valueProp="customerLogoId"
						 label="logoName"
						 track-by="logoName"
						 placeholder="Choose a logo..." />
			<template #footer>
				<Button label="Cancel" class="cancel" @click="cancelSelectExisting" />
				<Button label="Assign" :disabled="!selectExistingLogoId" @click="confirmSelectExisting" />
			</template>
		</Dialog>

		<Dialog class="manage-logo-popup" v-model:visible="showManageLogoPopup" :modal="true" header="Manage logo">
			<div class="flex flex-column align-items-center gap-3">
				<div class="logo-preview-container">
					<img :src="manageLogoPreviewSrc" class="logo-preview-image" />
					<SplitButton v-if="!isCompassDefaultLogo && manageCustomer && manageCustomer.companyId"
								 icon="pi pi-upload"
								 class="logo-upload-overlay"
								 :model="manageLogoUploadMenuItems()"
								 @click="chooseManageFile"
								 v-tooltip.top="'Update image'" />
				</div>

				<div class="w-100pc" v-if="!isCompassDefaultLogo">
					<label>Logo name</label>
					<InputText v-model="manageLogoName" class="w-100pc" />
				</div>
			</div>
			<template #footer>
				<template v-if="isCompassDefaultLogo">
					<Button label="Close" icon="pi pi-check" @click="showManageLogoPopup = false" />
				</template>
				<template v-else>
					<div class="flex justify-content-between w-100pc">
						<Button label="Delete" icon="pi pi-trash" class="p-button-danger" @click="deleteFromManagePopup" />
						<div class="flex gap-2">
							<Button label="Cancel" icon="pi pi-times" class="cancel" @click="cancelManageLogo" />
							<Button label="Save" icon="pi pi-save" @click="saveManageLogo" />
						</div>
					</div>
				</template>
			</template>
		</Dialog>

		<input type="file" ref="manageFileInput" accept=".svg" class="hidden" @change="onManageFileSelected" />
	</div>
</template>

<script lang="ts">
	import { Vue, Options } from 'vue-class-component';
	import { AssignCompanyBwofTemplateLogoCommand, CustomerLogoOption, CustomerLogoData, CustomerLogosExcelExportRequest, LogoReference, PropertyLogoOverrideSummary, SyncAffectedPropertiesExcelExportRequest, SyncPropertiesToCustomerLogoCommand, UpdateCompanyBwofTemplateLogoCommand, UpdateCompanyDisplayCompassLogoCommand, UpdateCustomerLogoCommand } from '@/api-client/api-client'
	import { CompassApi } from '@/api-client/CompassApiProxy';
	import { GetInlineLogoListLimit, GetMaxLogoFileSizeBytes } from '@/utils/CompassConstants';
	import { DownloadFromFileResponse } from '@/utils/DownloadFromFileResponse';
	import { Navigation } from '@/utils/Navigation';
	import filterListBySearchString from '@/utils/StringSearch'
	import Toast from '@/utils/Toast';

	@Options({
		name: 'CustomerLogos',
	})

	export default class CustomerLogos extends Vue {

		navigation = Navigation;

		customerListFilter = "";
		manageLogoName = "";
        sortField: 'companyId' | 'companyName' | 'logoName' | 'displayCompassLogo' | null = null;
		logoFilter: number | null = null;
		sortDirection: 1 | -1 = 1;

		customers: CustomerLogoData[] = [];
		logoOptions: CustomerLogoOption[] = [];
        syncAffectedProperties: PropertyLogoOverrideSummary[] = [];

		pendingCompany: CustomerLogoData | null = null;
        syncCustomer: CustomerLogoData | null = null;
        pendingRenameTo: string | null = null;
        manageCustomer: CustomerLogoData | null = null;
        manageStagedImageBase64: string | null = null;
        compassDefaultLogoSvg: string | null = null;
        expandedLogoLists = new Set<string>();
        selectExistingCustomer: CustomerLogoData | null = null;
        selectExistingLogoId: number | null = null;

        includeDeletedCustomers = false;
		showSyncConfirm = false;
        showManageLogoPopup = false;
        showSelectExisting = false;
        isCompassDefaultLogo = false;

		readonly inlineLogoListLimit = GetInlineLogoListLimit();

		created() {
			this.loadCustomers();
			this.loadLogoOptions();
			this.loadCompassDefaultLogo();
		}

		get filteredCustomers(): CustomerLogoData[] {
			let list = filterListBySearchString(this.customers, this.customerListFilter, data => data.companyName ?? "");

			if (this.logoFilter != null)
				list = list.filter(customer => customer.customerLogoId === this.logoFilter);

			// Default to grouping by whether a logo is set
			const field = this.sortField ?? 'logoName';
			const direction = this.sortField ? this.sortDirection : 1;

			list = [...list].sort((first, second) => {
				if (field === 'logoName') {
					const firstHasLogo = first.bwofTemplateLogo ? 1 : 0;
					const secondHasLogo = second.bwofTemplateLogo ? 1 : 0;

					if (firstHasLogo !== secondHasLogo)
						return (secondHasLogo - firstHasLogo) * direction;

					return (first.companyName ?? '').localeCompare(second.companyName ?? '') * direction;
				}

				// Logos with the Compass logo switched off first
				if (field === 'displayCompassLogo') {
					const firstShowsCompassLogo = first.displayCompassLogo ? 1 : 0;
					const secondShowsCompassLogo = second.displayCompassLogo ? 1 : 0;

					if (firstShowsCompassLogo !== secondShowsCompassLogo)
						return (firstShowsCompassLogo - secondShowsCompassLogo) * direction;

					return (first.companyName ?? '').localeCompare(second.companyName ?? '') * direction;
				}

				const firstValue = first[field] ?? '';
				const secondValue = second[field] ?? '';

				if (firstValue < secondValue) return -1 * direction;
				if (firstValue > secondValue) return 1 * direction;
				return 0;
			});

			return list;
		}

		toggleSort(field: 'companyId' | 'companyName' | 'logoName' | 'displayCompassLogo'): void {
			if (this.sortField === field)
				this.sortDirection = this.sortDirection === 1 ? -1 : 1;
			else {
				this.sortField = field;
				this.sortDirection = 1;
			}
		}

		sortIconFor(field: 'companyId' | 'companyName' | 'logoName' | 'displayCompassLogo'): string {
			if (this.sortField !== field)
				return 'pi pi-sort';

			return this.sortDirection === 1 ? 'pi pi-sort-up' : 'pi pi-sort-down';
		}

		get isDesktop(): boolean {
			return window.innerWidth >= 992;
		}

		// Computed to simplify paginator on mobile
		get paginatorTemplate(): string {
			return this.isDesktop ?
				"CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
				: "PrevPageLink NextPageLink";
		}

		get manageLogoPreviewSrc(): string {
			if (this.isCompassDefaultLogo)
				return this.compassDefaultLogoSvg ? `data:image/svg+xml;base64,${this.compassDefaultLogoSvg}` : "";

			const base64 = this.manageStagedImageBase64 ?? this.manageCustomer?.bwofTemplateLogo;
			return base64 ? `data:image/svg+xml;base64,${base64}` : "";
		}

		loadCustomers(): Promise<CustomerLogoData[]> {
			const apiClient = CompassApi.companyApi(this.$store);

			return apiClient.getCustomerLogos(this.includeDeletedCustomers, false)
				.then(result => {
					this.customers = result;
					return result;
				});
		}

		loadLogoOptions(): void {
			const apiClient = CompassApi.companyApi(this.$store);

			apiClient.getCustomerLogoOptions()
				.then(result => {
					this.logoOptions = result;
				});
		}

		loadCompassDefaultLogo(): void {
			const apiClient = CompassApi.companyApi(this.$store);

			apiClient.getCustomerLogos(false, true)
				.then(result => {
					this.compassDefaultLogoSvg = result.find(customer => customer.isCompassDefault)?.bwofTemplateLogo ?? null;
				});
		}

		exportReport(): void {
			const apiClient = CompassApi.companyApi(this.$store);

			const request = new CustomerLogosExcelExportRequest();
			request.includeDeleted = this.includeDeletedCustomers;
			request.includeUnassignedLogos = false;
			request.records = this.filteredCustomers;

			apiClient.customerLogosExcelExport(request)
				.then(result => {
					if (result)
						DownloadFromFileResponse(result);

					Toast.RemoveAllGroups();
					Toast.Success('Customer templates', 'The export was successful.');
				})
				.catch(error => {
					Toast.ApiError('Error exporting to excel', error);
				});
		}

		exportSyncAffectedProperties(): void {
			const apiClient = CompassApi.companyApi(this.$store);

			const request = new SyncAffectedPropertiesExcelExportRequest();
			request.companyName = this.syncCustomer?.companyName;
			request.records = this.syncAffectedProperties;

			apiClient.syncAffectedPropertiesExcelExport(request)
				.then(result => {
					if (result)
						DownloadFromFileResponse(result);

					Toast.RemoveAllGroups();
					Toast.Success('Properties to sync', 'The export was successful.');
				})
				.catch(error => {
					Toast.ApiError('Error exporting to excel', error);
				});
		}

		logoPreviewSrc(customer: CustomerLogoData): string {
			return `data:image/svg+xml;base64,${customer.bwofTemplateLogo}`;
		}

		private trimSvgWhitespace(base64Svg: string): string {
			try {
				const svgText = decodeURIComponent(escape(atob(base64Svg)));
				const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
				const svg = doc.documentElement as unknown as SVGSVGElement;

				if (svg.tagName.toLowerCase() !== 'svg' || doc.querySelector('parsererror'))
					return base64Svg;

				// getBBox() only returns meaningful values if measured off-screen
                const SvgSvgElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg') as SVGSVGElement;
                SvgSvgElement.style.position = 'absolute';
                SvgSvgElement.style.visibility = 'hidden';
                SvgSvgElement.style.width = '0';
                SvgSvgElement.style.height = '0';
                document.body.appendChild(SvgSvgElement);

                const importedSvgElement = document.importNode(svg, true) as SVGSVGElement;
                SvgSvgElement.appendChild(importedSvgElement);
                const bbox = importedSvgElement.getBBox();
                SvgSvgElement.remove();

				if (!bbox || bbox.width <= 0 || bbox.height <= 0)
					return base64Svg;

				const padding = Math.max(bbox.width, bbox.height) * 0.04;
				svg.setAttribute('viewBox', `${bbox.x - padding} ${bbox.y - padding} ${bbox.width + padding * 2} ${bbox.height + padding * 2}`);
				svg.removeAttribute('width');
				svg.removeAttribute('height');

				const trimmedSvgText = new XMLSerializer().serializeToString(svg);
				return btoa(unescape(encodeURIComponent(trimmedSvgText)));
			}
			catch {
				return base64Svg;
			}
		}

		uploadMenuItemsFor(customer: CustomerLogoData) {
			return [
				{
					label: 'Use existing logo',
					icon: 'pi pi-list',
					command: () => this.openSelectExisting(customer)
				}
			];
		}

		manageLogoUploadMenuItems() {
			const customer = this.manageCustomer;
			if (!customer)
				return [];

			return [
				{
					label: 'Use existing logo',
					icon: 'pi pi-list',
					command: () => {
						this.showManageLogoPopup = false;
						this.openSelectExisting(customer);
					}
				}
			];
		}

		openSelectExisting(customer: CustomerLogoData): void {
			this.selectExistingCustomer = customer;
			this.selectExistingLogoId = null;
			this.showSelectExisting = true;
		}

		cancelSelectExisting(): void {
			this.showSelectExisting = false;
			this.selectExistingCustomer = null;
			this.selectExistingLogoId = null;
		}

		confirmSelectExisting(): void {
			const customer = this.selectExistingCustomer;
			const customerLogoId = this.selectExistingLogoId;

			this.showSelectExisting = false;
			this.selectExistingCustomer = null;
			this.selectExistingLogoId = null;

			if (!customer || !customer.companyId || !customerLogoId)
				return;

			const apiClient = CompassApi.companyApi(this.$store);
			const command = new AssignCompanyBwofTemplateLogoCommand();
			command.companyId = customer.companyId;
			command.customerLogoId = customerLogoId;

			apiClient.assignCompanyBwofTemplateLogo(command)
				.then(() => {
					this.loadCustomers();
					Toast.RemoveAllGroups();
					Toast.Success('Customer logo', `Logo assigned to ${customer.companyName}.`);
				})
				.catch(error => {
					Toast.ApiError('Could not assign logo', error);
				});
		}

		chooseFile(customer: CustomerLogoData): void {
			this.pendingCompany = customer;
			(this.$refs.fileInput as HTMLInputElement).click();
		}

		onFileSelected(event: Event): void {
			const input = event.target as HTMLInputElement;
			const file = input.files?.[0];
			const customer = this.pendingCompany;

			// Reset so selecting the same file again still fires 'change'
			input.value = "";

			if (!file || !customer)
				return;

			if (!file.name.toUpperCase().endsWith('.SVG')) {
				Toast.RemoveAllGroups();
				Toast.Error('Invalid file', 'Only SVG files can be uploaded as a customer logo.');
				return;
			}

			if (file.size > GetMaxLogoFileSizeBytes()) {
				Toast.RemoveAllGroups();
				Toast.Error('File too large', 'Logo files must be smaller than 500 KB.');
				return;
			}

			const reader = new FileReader();
			reader.onload = () => {
				const dataUri = reader.result as string;
				const base64Svg = dataUri.substring(dataUri.indexOf(',') + 1);
				this.requestSaveLogo(customer, this.trimSvgWhitespace(base64Svg));
			};
			reader.readAsDataURL(file);
		}

		requestSaveLogo(customer: CustomerLogoData, base64Svg: string | null): void {
			this.saveLogo(customer, base64Svg);
		}

		saveLogo(customer: CustomerLogoData, base64Svg: string | null): void {
			if (!customer.companyId)
				return;

			const apiClient = CompassApi.companyApi(this.$store);
			const companyId = customer.companyId;

			const command = new UpdateCompanyBwofTemplateLogoCommand();
			command.companyId = companyId;
			command.bwofTemplateLogo = base64Svg ?? undefined;

			apiClient.updateCompanyBwofTemplateLogo(command)
				.then(() => {
					Toast.RemoveAllGroups();
					Toast.Success('Customer logo', base64Svg ? `Logo updated for ${customer.companyName}.` : `Logo removed for ${customer.companyName}.`);

					const renameTo = this.pendingRenameTo;
					this.pendingRenameTo = null;

					// Look up the logo id after saving, as a shared logo gets forked into a new one
					this.loadCustomers().then(result => {
						const savedLogoId = result.find(saved => saved.companyId === companyId)?.customerLogoId;
						if (renameTo && savedLogoId)
							this.renameLogo(savedLogoId, renameTo);
					});
				})
				.catch(error => {
					Toast.ApiError('Could not save logo', error);
				});
		}

		renameLogo(customerLogoId: number, logoName: string): void {
			const apiClient = CompassApi.companyApi(this.$store);
			const command = new UpdateCustomerLogoCommand();
			command.customerLogoId = customerLogoId;
			command.logoName = logoName;

			apiClient.updateCustomerLogo(command)
				.then(() => {
					this.loadCustomers();
				})
				.catch(error => {
					Toast.ApiError('Could not rename logo', error);
				});
		}

		canSetDisplayCompassLogo(customer: CustomerLogoData): boolean {
			return !!customer.companyId && !!customer.bwofTemplateLogo && !customer.isCompassDefault;
		}

		displayCompassLogoTooltip(customer: CustomerLogoData): string {
			if (!this.canSetDisplayCompassLogo(customer))
				return 'Upload a customer logo first. The Compass logo always shows when using the Compass default.';

			return '';
		}

		setDisplayCompassLogo(customer: CustomerLogoData): void {
			const apiClient = CompassApi.companyApi(this.$store);
			const command = new UpdateCompanyDisplayCompassLogoCommand();
			command.companyId = customer.companyId;
			command.displayCompassLogo = customer.displayCompassLogo;

			apiClient.updateCompanyDisplayCompassLogo(command)
				.then(() => {
					Toast.RemoveAllGroups();
					Toast.Success('Customer logo', `Compass logo setting updated for ${customer.companyName}.`);
				})
				.catch(error => {
					Toast.ApiError('Could not update logo', error);
					// Revert the switch
					this.loadCustomers();
				});
		}

		syncProperties(customer: CustomerLogoData): void {
			if (!customer.companyId)
				return;

			const apiClient = CompassApi.companyApi(this.$store);

			apiClient.getPropertiesWithLogoOverride(customer.companyId).then(properties => {
				this.syncCustomer = customer;
				this.syncAffectedProperties = properties;
				this.showSyncConfirm = true;
			});
		}

		cancelSyncConfirm(): void {
			this.showSyncConfirm = false;
			this.syncCustomer = null;
			this.syncAffectedProperties = [];
		}

		confirmSync(): void {
			const customer = this.syncCustomer;
			this.showSyncConfirm = false;

			if (!customer || !customer.companyId)
				return;

			const apiClient = CompassApi.companyApi(this.$store);
			const command = new SyncPropertiesToCustomerLogoCommand();
			command.companyId = customer.companyId;

			apiClient.syncPropertiesToCustomerLogo(command)
				.then(count => {
					this.loadCustomers();
					Toast.RemoveAllGroups();
					Toast.Success('Customer logo', `${count} propert${count === 1 ? 'y' : 'ies'} synced to ${customer.companyName}'s logo.`);
				})
				.catch(error => {
					Toast.ApiError('Could not sync properties', error);
				});
		}

		visibleLogoRefs(items: LogoReference[] | undefined, companyId: number | undefined, listKey: string): LogoReference[] {
			if (!items)
				return [];

			if (this.isLogoListExpanded(companyId, listKey))
				return items;

			return items.slice(0, this.inlineLogoListLimit);
		}

		isLogoListExpanded(companyId: number | undefined, listKey: string): boolean {
			return this.expandedLogoLists.has(`${listKey}-${companyId}`);
		}

		expandLogoList(companyId: number | undefined, listKey: string): void {
			this.expandedLogoLists.add(`${listKey}-${companyId}`);
		}

		openCompassLogoPreview(): void {
			this.manageCustomer = null;
			this.isCompassDefaultLogo = true;
			this.manageLogoName = "";
			this.manageStagedImageBase64 = null;
			this.showManageLogoPopup = true;
		}

		openManageLogo(customer: CustomerLogoData): void {

			// The Compass Default logo isn't editable
			if (customer.isCompassDefault) {
				this.openCompassLogoPreview();
				return;
			}

			this.manageCustomer = customer;
			this.isCompassDefaultLogo = false;
			this.manageLogoName = customer.logoName ?? customer.companyName ?? "";
			this.manageStagedImageBase64 = null;
			this.showManageLogoPopup = true;
		}

		chooseManageFile(): void {
			(this.$refs.manageFileInput as HTMLInputElement).click();
		}

		onManageFileSelected(event: Event): void {
			const input = event.target as HTMLInputElement;
			const file = input.files?.[0];

			// Reset so selecting the same file again still fires 'change'
			input.value = "";

			if (!file)
				return;

			if (!file.name.toUpperCase().endsWith('.SVG')) {
				Toast.RemoveAllGroups();
				Toast.Error('Invalid file', 'Only SVG files can be uploaded as a customer logo.');
				return;
			}

			if (file.size > GetMaxLogoFileSizeBytes()) {
				Toast.RemoveAllGroups();
				Toast.Error('File too large', 'Logo files must be smaller than 500 KB.');
				return;
			}

			const reader = new FileReader();
			reader.onload = () => {
				const dataUri = reader.result as string;
				const base64Svg = dataUri.substring(dataUri.indexOf(',') + 1);
				this.manageStagedImageBase64 = this.trimSvgWhitespace(base64Svg);
			};
			reader.readAsDataURL(file);
		}

		cancelManageLogo(): void {
			this.showManageLogoPopup = false;
			this.manageCustomer = null;
			this.manageStagedImageBase64 = null;
		}

		deleteFromManagePopup(): void {
			const customer = this.manageCustomer;

			this.showManageLogoPopup = false;
			this.manageCustomer = null;
			this.manageStagedImageBase64 = null;

			if (!customer)
				return;

			this.requestSaveLogo(customer, null);
		}

		saveManageLogo(): void {
			const customer = this.manageCustomer;
			if (!customer)
				return;

			const newName = this.manageLogoName.trim();
			const currentName = customer.logoName ?? customer.companyName ?? "";
			const nameChanged = newName.length > 0 && newName !== currentName;
			const stagedImage = this.manageStagedImageBase64;

			this.showManageLogoPopup = false;
			this.manageCustomer = null;
			this.manageStagedImageBase64 = null;

			if (stagedImage) {
				this.pendingRenameTo = nameChanged ? newName : null;
				this.requestSaveLogo(customer, stagedImage);
				return;
			}

			if (nameChanged && customer.customerLogoId)
				this.renameLogo(customer.customerLogoId, newName);
		}

	}
</script>

<style scoped>
    .manage-logo-popup {
        max-width: 560px;
    }

    .confirm-dialog {
        max-width: 480px;
    }

    .confirm-dialog :deep(.multiselect) {
        --ms-max-height: 220px;
    }

    .sync-summary {
        overflow-wrap: break-word;
    }

    .col-id {
        width: 80px;
    }

    .col-logo {
        width: 160px;
    }

    .col-actions {
        width: 260px;
    }

    .customer-sub-line {
        font-size: 12px;
    }

    .logo-thumbnail {
        width: 140px;
        height: 60px;
        object-fit: contain;
    }

    .header-label {
        color: #5b142a;
        font-weight: 700;
    }

	.sortable-header {
		cursor: pointer;
		user-select: none;
	}

	.sortable-header i {
		font-size: 12px;
		margin-left: 4px;
	}

	.row-deleted, .row-deleted a {
		color: #aaa;
	}

	.logo-filter-container {
		border: 1px solid #d1d5db;
		border-radius: 4px;
		background: #fff;
		padding-left: 0.5rem;
	}

    .logo-filter-container > i:first-of-type {
        color: #6c757d;
    }

    .logo-filter-container :deep(.multiselect) {
        border: none;
        background: transparent;
    }

	.logo-preview-container {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 250px;
		height: 150px;
	}

	.logo-preview-image {
		max-width: 100%;
		max-height: 100%;
	}

	.logo-upload-overlay {
		position: absolute;
		top: 0px;
		right: 0px;
	}

	.logo-upload-overlay :deep(.p-button) {
		background: #5b142a;
		border-color: #5b142a;
		color: white;
		width: 2rem;
		height: 2rem;
	}

	.logo-upload-overlay :deep(.p-button:hover) {
		background: #721f3d;
		border-color: #721f3d;
	}

	.logo-upload-overlay :deep(.p-splitbutton-menubutton) {
		width: 1.25rem;
	}
</style>

<style>
	.confirm-dialog .p-dialog-content {
		overflow: visible;
	}
</style>